import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { NavItems } from "@helpers/navigation";
import { AssessmentBegin } from "./assessmentBegin";
import { useApi } from "@services/useApi";
import { useAppDispatch } from "@app/hooks";
import { delayedAB, getCuedRecalls, getFreeRecalls, getPresentTargets, fullFlow } from "@utils/flows";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Colors } from "@styles/colors";
import { LHero } from "@components";
import { disableKeyBoard } from "@helpers/fullScreenHelper";
import { AssessmentStart } from "./assessmentStart";
import { reinstateGlobalSettings } from "@store/globalSettingsSlice";
import { setLocale } from "react-i18nify";
import { LanguageId } from "@store/generalTypes";
import {
    AssessmentPracticeInstruction,
    AssessmentBeginPracticeRun,
    AssessmentTestRun,
    AssessmentClosingVideo,
    AssessmentComplete,
    AssessmentCuedRecalls,
    AssessmentDelayedRecall,
    AssessmentDeviceCheckInstruction,
    AssessmentFreeRecall,
    AssessmentInstruction,
    AssessmentInstructionCountdown,
    AssessmentPresentTargets
} from ".";
import { setAssessmentFlow } from "@store/assessmentFlowSlice";
import { updateAssessment } from "@store/assessmentSlice";
import { setAssessmentForm } from "@store/assessmentFormSlice";
import { ExamineeContactInfo, updateContactInfo } from "@fadmin/tabs/assessments/store/newExamineeSlice";
import { StorageAssessmentForm } from "./assessmentWelcome";
import { AssessmentChecks } from "./assessmentChecks";
import { isRemoteAssessmentComplete } from "../helpers/remoteAssessmentCompletion";

export const AssessmentExamineeLanding = (): JSX.Element => {
    const [isLoading, setIsLoading] = useState(true);

    const { getAssessment, getAssessmentForm } = useApi();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const isCompletedRemoteAssessment = isRemoteAssessmentComplete(location.pathname);

    useEffect(() => {
        const fetchAssessmentData = async (): Promise<void> => {
            if (isCompletedRemoteAssessment) {
                setIsLoading(false);
                return;
            }

            const storageAssessmentForm = JSON.parse(localStorage.getItem("assessmentForm")!) as StorageAssessmentForm;
            const {
                assessmentFormId,
                assessmentId,
                flow,
                language: languageId,
                examineeName,
                globalSettings
            } = storageAssessmentForm;

            dispatch(reinstateGlobalSettings(globalSettings));

            // TODO: validate routes
            if (assessmentFormId && assessmentId) {
                // TODO: for QA testing
                switch (flow) {
                    case 1:
                        dispatch(setAssessmentFlow(fullFlow));
                        break;
                    case 2:
                        dispatch(setAssessmentFlow([getPresentTargets(0, 1)]));
                        break;
                    case 3:
                        dispatch(setAssessmentFlow([getFreeRecalls(0, 1)]));
                        break;
                    case 4:
                        dispatch(setAssessmentFlow(delayedAB));
                        break;
                    case 5:
                        dispatch(setAssessmentFlow(delayedAB));
                        break;
                    case 6:
                        dispatch(setAssessmentFlow([getCuedRecalls(0, 1)]));
                        break;
                    case 7:
                        break;
                    default:
                        break;
                }

                disableKeyBoard();

                const locale = LanguageId[Number(languageId)];

                setLocale(locale ?? "en");

                const assessment = await getAssessment(assessmentId);
                assessment.id = assessmentId;
                dispatch(updateAssessment(assessment));

                const parseExamineeName = examineeName?.split(" ");

                const contactInfo: ExamineeContactInfo = {
                    firstName: parseExamineeName[0],
                    lastName: parseExamineeName[1],
                    id: 0,
                    phoneNumber: "",
                    email: ""
                };
                dispatch(updateContactInfo(contactInfo));

                const instructions = await getAssessmentForm(assessmentFormId, languageId);
                dispatch(setAssessmentForm(instructions));
            }

            setIsLoading(false);
        };

        void fetchAssessmentData();
    }, [isCompletedRemoteAssessment]);

    return (
        <>
            {isLoading ? (
                <LHero centered contentCentered size="fullheight">
                    <div className="is-flex is-flex-direction-column is-justify-content-center is-align-items-center">
                        <FontAwesomeIcon icon={faSpinner} size="6x" color={Colors.darkBlue} className="fa-spin" />
                    </div>
                </LHero>
            ) : isCompletedRemoteAssessment ? (
                <Navigate to={NavItems.Assessments.AssessmentComplete.path} replace />
            ) : (
                <Routes>
                    <Route
                        path={NavItems.Assessments.PracticeInstruction.relativePath}
                        element={<AssessmentPracticeInstruction />}
                    />
                    <Route
                        path={NavItems.Assessments.PracticeBeginRun.relativePath}
                        element={<AssessmentBeginPracticeRun />}
                    />
                    <Route
                        path={NavItems.Assessments.DeviceCheckInstruction.relativePath}
                        element={<AssessmentDeviceCheckInstruction />}
                    />
                    <Route path={NavItems.Assessments.AssessmentChecks.relativePath} element={<AssessmentChecks />} />
                    <Route path={NavItems.Assessments.PracticeTestRun.relativePath} element={<AssessmentTestRun />} />
                    <Route path={NavItems.Assessments.Begin.relativePath} element={<AssessmentBegin />} />
                    <Route path={NavItems.Assessments.Instruction.relativePath} element={<AssessmentInstruction />} />
                    <Route
                        path={NavItems.Assessments.InstructionCountdown.relativePath}
                        element={<AssessmentInstructionCountdown />}
                    />
                    <Route
                        path={NavItems.Assessments.AssessmentPresentTargets.relativePath}
                        element={<AssessmentPresentTargets />}
                    />
                    <Route
                        path={NavItems.Assessments.AssessmentFreeRecall.relativePath}
                        element={<AssessmentFreeRecall />}
                    />
                    <Route
                        path={NavItems.Assessments.AssessmentCuedRecalls.relativePath}
                        element={<AssessmentCuedRecalls />}
                    />
                    <Route
                        path={NavItems.Assessments.AssessmentComplete.relativePath}
                        element={<AssessmentComplete />}
                    />
                    <Route
                        path={NavItems.Assessments.AssessmentClosingVideo.relativePath}
                        element={<AssessmentClosingVideo />}
                    />
                    <Route
                        path={NavItems.Assessments.AssessmentDelayedRecall.relativePath}
                        element={<AssessmentDelayedRecall />}
                    />
                    <Route path={NavItems.Assessments.Start.relativePath} element={<AssessmentStart />} />
                </Routes>
            )}
        </>
    );
};
